"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DayOfMonthFromPaddedString = exports.DayOfMonth = exports.dayOfMonthPattern = exports.MonthNumber = exports.monthNumberPattern = exports.TimezoneOffset = exports.Timestamp13FromString = exports.TimelessDateFromTimelessUtcIsoDateString = exports.TimelessUtcIsoDateString = exports.DateFromUtcIsoDateString = exports.UtcIsoDateString = exports.TolerantValidDateFromIso8601String = exports.DateFromFirestoreTimestamp = exports.FirestoreTimestamp = exports.ValidDateFromIso8601String = exports.Iso8601String = exports.Timestamp = exports.ValidDateFromNumber = exports.MillisecondsSinceEpoch = void 0;
exports.toTimeless = toTimeless;
const effect_1 = require("effect");
const schema_1 = require("./schema");
const NAMESPACE = 'Date';
exports.MillisecondsSinceEpoch = effect_1.Schema.Number.pipe(effect_1.Schema.between(-8_640_000_000_000_000, 8_640_000_000_000_000)).annotations({
    identifier: `${NAMESPACE}.MillisecondsSinceEpoch`,
    description: 'The number of milliseconds elapsed since the 1st of January, 1970 UTC (Unix Epoch). ' +
        'This number can be either positive or negative. ' +
        'When used as the singular parameter for the Date constructor, ' +
        'the ECMAScript standard stipulates the maximum delta between the constructed date ' +
        'and the Unix Epoch to be 100,000,000 days (8,640,000,000,000,000 milliseconds).',
    documentation: 'https://262.ecma-international.org/5.1/#sec-15.9.1.1',
});
/**
 * @internal Use {@link Timestamp} instead.
 */
exports.ValidDateFromNumber = exports.MillisecondsSinceEpoch.pipe(effect_1.Schema.compose(effect_1.Schema.DateFromNumber), effect_1.Schema.compose(effect_1.Schema.ValidDateFromSelf)).annotations({
    identifier: `${NAMESPACE}.ValidDateFromNumber`,
});
exports.Timestamp = exports.ValidDateFromNumber.annotations({
    identifier: `${NAMESPACE}.Timestamp`,
    description: 'A Date encoded to the number of milliseconds since the Unix Epoch',
});
exports.Iso8601String = effect_1.Schema.String.annotations({
    identifier: `${NAMESPACE}.Iso8601String`,
    description: 'String conforming to ISO 8601',
    documentation: 'https://tc39.es/ecma262/multipage/numbers-and-dates.html#sec-date-time-string-format',
});
exports.ValidDateFromIso8601String = exports.Iso8601String.pipe(effect_1.Schema.compose(effect_1.Schema.DateFromString), effect_1.Schema.compose(effect_1.Schema.ValidDateFromSelf)).annotations({
    identifier: `${NAMESPACE}.ValidDateFromIso8601String`,
});
exports.FirestoreTimestamp = effect_1.Schema.Union(effect_1.Schema.Struct({ seconds: effect_1.Schema.Number, nanoseconds: effect_1.Schema.Number }), effect_1.Schema.Struct({ _seconds: effect_1.Schema.Number, _nanoseconds: effect_1.Schema.Number })).annotations({
    identifier: `${NAMESPACE}.FirestoreTimestamp`,
    description: 'Firestore Timestamp shape, as exported by the Firebase Admin SDK. ' +
        'Accepted for legacy Firestore-migrated records only; new code should not produce this shape.',
});
exports.DateFromFirestoreTimestamp = exports.FirestoreTimestamp.pipe(effect_1.Schema.transform(effect_1.Schema.ValidDateFromSelf, {
    strict: true,
    decode: ts => {
        const seconds = 'seconds' in ts ? ts.seconds : ts._seconds;
        const nanoseconds = 'nanoseconds' in ts ? ts.nanoseconds : ts._nanoseconds;
        return new Date(seconds * 1000 + nanoseconds / 1e6);
    },
    encode: date => ({
        seconds: Math.floor(date.getTime() / 1000),
        nanoseconds: (date.getTime() % 1000) * 1e6,
    }),
})).annotations({ identifier: `${NAMESPACE}.DateFromFirestoreTimestamp` });
exports.TolerantValidDateFromIso8601String = effect_1.Schema.Union(exports.ValidDateFromIso8601String, exports.DateFromFirestoreTimestamp).annotations({
    identifier: `${NAMESPACE}.TolerantValidDateFromIso8601String`,
    description: 'Accepts an ISO 8601 string or a legacy Firestore Timestamp object; ' +
        'encodes back to ISO 8601 string (first union branch wins on encode).',
    jsonSchema: { type: 'string', format: 'date-time' },
});
exports.UtcIsoDateString = effect_1.Schema.String.pipe(effect_1.Schema.pattern(/^[-+]?\d+-\d{1,2}-\d{1,2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/)).annotations({
    identifier: `${NAMESPACE}.UtcIsoDateString`,
    description: 'String conforming to the ISO 8601 extended format with a timezone offset of 0 (UTC)',
    documentation: 'https://tc39.es/ecma262/multipage/numbers-and-dates.html#sec-date-time-string-format',
    examples: [
        '2024-01-01T12:34:56Z',
        '2024-01-01T12:34:56.7Z',
        '2024-01-01T12:34:56.78Z',
        '2024-01-01T12:34:56.789Z',
    ],
});
exports.DateFromUtcIsoDateString = exports.UtcIsoDateString.pipe(effect_1.Schema.compose(effect_1.Schema.Date)).annotations({
    identifier: `${NAMESPACE}.DateFromUtcIsoDateString`,
    jsonSchema: {
        type: 'string',
        format: 'date-time',
    },
});
exports.TimelessUtcIsoDateString = effect_1.Schema.String.pipe(effect_1.Schema.pattern(/^[-+]?\d+-\d{1,2}-\d{1,2}T00:00:00(?:\.0{1,3})?Z$/)).annotations({
    identifier: `${NAMESPACE}.TimelessUtcIsoDateString`,
    description: 'String conforming to the ISO 8601 extended format with a timezone offset of 0 (UTC) and all time fields set to 0',
    examples: [
        '2024-01-01T00:00:00Z',
        '2024-01-01T00:00:00.0Z',
        '2024-01-01T00:00:00.00Z',
        '2024-01-01T00:00:00.000Z',
    ],
});
/**
 * Returns a new `Date` identical to the provided `Date`, but with all time fields set to `0`.
 */
function toTimeless(date) {
    const newDate = new Date(date.getTime());
    newDate.setUTCHours(0, 0, 0, 0);
    return newDate;
}
exports.TimelessDateFromTimelessUtcIsoDateString = exports.TimelessUtcIsoDateString.pipe(effect_1.Schema.compose(effect_1.Schema.Date)).annotations({
    identifier: `${NAMESPACE}.TimelessDateFromTimelessUtcIsoDateString`,
    arbitrary: () => FC => FC.date({ noInvalidDate: true }).map(toTimeless),
    jsonSchema: {
        type: 'string',
        format: 'date',
    },
});
exports.Timestamp13FromString = effect_1.Schema.String.pipe(effect_1.Schema.length(13), effect_1.Schema.compose(effect_1.Schema.NumberFromString), effect_1.Schema.compose(effect_1.Schema.DateFromNumber)).annotations({
    identifier: `${NAMESPACE}.Timestamp13FromString`,
    description: 'A timestamp with a fixed number of exactly 13 digits',
    examples: [new Date(1234567890123)],
    arbitrary: () => FC => FC.date({ min: new Date(1000000000000), max: new Date(9999999999999) }),
});
exports.TimezoneOffset = effect_1.Schema.Number.pipe(effect_1.Schema.between(-12, 12)).annotations({
    identifier: `${NAMESPACE}.TimezoneOffset`,
});
/**
 * A regular expression pattern for any number `[01...12]` (inclusive), start-padded with a `0`.
 */
exports.monthNumberPattern = String.raw `(0[1-9])|(1[0-2])`;
exports.MonthNumber = effect_1.Schema.Literal(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12).annotations({
    identifier: `${NAMESPACE}.MonthNumber`,
});
/**
 * A regular expression pattern for any number `[01...31]` (inclusive), start-padded with a `0`.
 */
exports.dayOfMonthPattern = String.raw `(0[1-9])|([1-2]\d)|(3[0-1])`;
exports.DayOfMonth = effect_1.Schema.Int.pipe(effect_1.Schema.between(1, 31)).annotations({
    identifier: `${NAMESPACE}.DayOfMonth`,
});
exports.DayOfMonthFromPaddedString = effect_1.Schema.String.pipe(effect_1.Schema.pattern(new RegExp(String.raw `^${exports.dayOfMonthPattern}$`)), effect_1.Schema.compose((0, schema_1.EncodePadStart)(2, '0')), effect_1.Schema.compose(effect_1.Schema.NumberFromString), effect_1.Schema.compose(exports.DayOfMonth)).annotations({ identifier: `${NAMESPACE}.DayOfMonthFromPaddedString` });
//# sourceMappingURL=date.js.map